import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '../components/AppShell';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, RefreshCw, Save, Clock, Play, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const FREQUENCY_OPTIONS = [
  { value: 'every 5 minutes', label: 'Every 5 minutes' },
  { value: 'every 15 minutes', label: 'Every 15 minutes' },
  { value: 'every 30 minutes', label: 'Every 30 minutes' },
  { value: 'every hour', label: 'Every hour' },
  { value: 'every 6 hours', label: 'Every 6 hours' },
  { value: 'every 12 hours', label: 'Every 12 hours' },
  { value: 'every day', label: 'Every day' },
];

const SYNC_TYPE_OPTIONS = [
  { value: 'incremental', label: 'Incremental' },
  { value: 'full', label: 'Full' },
];

const PROVIDER_CONFIG = {
  slack: {
    name: 'Slack',
    syncs: [
      { name: 'messages', label: 'Messages', description: 'Sync messages from project-linked channels' },
      { name: 'channels', label: 'Channels', description: 'Sync all Slack channels' },
      { name: 'users', label: 'Users', description: 'Sync Slack users' },
    ],
  },
  linear: {
    name: 'Linear',
    syncs: [
      { name: 'issues', label: 'Issues', description: 'Sync issues from project-linked teams' },
      { name: 'teams', label: 'Teams', description: 'Sync all Linear teams' },
      { name: 'projects', label: 'Projects', description: 'Sync Linear projects' },
      { name: 'users', label: 'Users', description: 'Sync Linear users' },
    ],
  },
  jira: {
    name: 'Jira',
    syncs: [
      { name: 'issues', label: 'Issues', description: 'Sync Jira issues' },
    ],
  },
  github: {
    name: 'GitHub',
    syncs: [
      { name: 'issues', label: 'Issues', description: 'Sync GitHub issues' },
      { name: 'pull_requests', label: 'Pull Requests', description: 'Sync pull requests' },
    ],
  },
};

export default function SyncSchedulesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [schedules, setSchedules] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [triggering, setTriggering] = useState({});
  const [syncStatus, setSyncStatus] = useState({});

  useEffect(() => {
    if (user?.role !== 'admin') {
      toast.error('Admin access required');
      navigate('/settings');
      return;
    }
    loadSchedules();
    loadSyncStatus();
    // Refresh status every 30 seconds
    const interval = setInterval(loadSyncStatus, 30000);
    return () => clearInterval(interval);
  }, [user, navigate]);

  const loadSchedules = async () => {
    try {
      const response = await api.get('/admin/sync-schedules');
      const schedulesMap = {};
      response.data.forEach(schedule => {
        const key = `${schedule.provider}:${schedule.sync_name}`;
        schedulesMap[key] = schedule;
      });
      setSchedules(schedulesMap);
    } catch (error) {
      toast.error('Failed to load sync schedules');
    } finally {
      setLoading(false);
    }
  };

  const loadSyncStatus = async () => {
    try {
      const response = await api.get('/admin/sync/status');
      const statusMap = {};
      if (response.data.last_syncs) {
        Object.entries(response.data.last_syncs).forEach(([key, status]) => {
          statusMap[key] = status;
        });
      }
      setSyncStatus(statusMap);
    } catch (error) {
      // Silently fail - status is optional
    }
  };

  const getSchedule = (provider, syncName) => {
    const key = `${provider}:${syncName}`;
    return schedules[key] || {
      provider,
      sync_name: syncName,
      frequency: 'every hour',
      auto_start: true,
      sync_type: 'incremental',
      enabled: true,
    };
  };

  const updateSchedule = (provider, syncName, updates) => {
    const key = `${provider}:${syncName}`;
    setSchedules(prev => ({
      ...prev,
      [key]: {
        ...getSchedule(provider, syncName),
        ...updates,
      },
    }));
  };

  const saveSchedule = async (provider, syncName) => {
    const schedule = getSchedule(provider, syncName);
    const key = `${provider}:${syncName}`;
    
    setSaving(prev => ({ ...prev, [key]: true }));
    
    try {
      await api.put(`/admin/sync-schedules/${provider}/${syncName}`, {
        frequency: schedule.frequency,
        auto_start: schedule.auto_start,
        sync_type: schedule.sync_type,
        enabled: schedule.enabled,
      });
      
      toast.success(`${PROVIDER_CONFIG[provider]?.name} ${schedule.sync_name} schedule updated`);
      await loadSchedules();
    } catch (error) {
      toast.error(`Failed to save schedule: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSaving(prev => ({ ...prev, [key]: false }));
    }
  };

  const triggerSync = async (provider, syncName) => {
    const key = `${provider}:${syncName}`;
    const schedule = getSchedule(provider, syncName);
    const syncType = schedule.sync_type || 'incremental';
    
    // Log for debugging
    console.log(`Triggering sync: provider=${provider}, sync_name=${syncName}, sync_type=${syncType}`, schedule);
    
    setTriggering(prev => ({ ...prev, [key]: true }));
    
    try {
      await api.post('/admin/sync/trigger', {
        provider,
        sync_name: syncName,
        sync_type: syncType,
      });
      
      toast.success(`Triggered ${syncType} ${syncName} sync for ${provider}`);
      setTimeout(loadSyncStatus, 2000); // Refresh status after 2 seconds
    } catch (error) {
      toast.error(`Failed to trigger sync: ${error.response?.data?.detail || error.message}`);
    } finally {
      setTriggering(prev => ({ ...prev, [key]: false }));
    }
  };

  const formatLastSync = (provider, syncName) => {
    const key = `${provider}:${syncName}`;
    const status = syncStatus[key];
    
    if (!status) return 'Never';
    
    const lastSync = status.last_sync_time;
    if (!lastSync) return 'Never';
    
    const date = new Date(lastSync);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  const getStatusBadge = (provider, syncName) => {
    const key = `${provider}:${syncName}`;
    const status = syncStatus[key];
    const schedule = getSchedule(provider, syncName);
    
    if (!schedule.enabled) {
      return <Badge variant="secondary">Disabled</Badge>;
    }
    
    if (!status) {
      return <Badge variant="outline">Unknown</Badge>;
    }
    
    const lastStatus = status.last_status;
    if (lastStatus === 'success') {
      return <Badge className="bg-green-500">Active</Badge>;
    } else if (lastStatus === 'running') {
      return <Badge className="bg-blue-500">Running</Badge>;
    } else if (lastStatus === 'failed') {
      return <Badge variant="destructive">Failed</Badge>;
    }
    
    return <Badge variant="outline">Pending</Badge>;
  };

  if (loading) {
    return (
      <AppShell>
        <div className="max-w-6xl mx-auto p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-muted rounded w-64"></div>
            <div className="h-64 bg-muted rounded"></div>
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/settings')}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Sync Schedules</h1>
            <p className="text-muted-foreground mt-1">
              Configure automatic sync schedules for your integrations
            </p>
          </div>
        </div>

        {Object.entries(PROVIDER_CONFIG).map(([provider, config]) => (
          <Card key={provider} className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">{config.name}</h2>
            </div>

            <div className="space-y-4">
              {config.syncs.map(({ name: syncName, label, description }) => {
                const schedule = getSchedule(provider, syncName);
                const key = `${provider}:${syncName}`;
                
                return (
                  <Card key={syncName} className="p-4 border-l-4 border-l-primary">
                    <div className="space-y-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold">{label}</h3>
                          <p className="text-sm text-muted-foreground">{description}</p>
                        </div>
                        {getStatusBadge(provider, syncName)}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Frequency</Label>
                          <Select
                            value={schedule.frequency}
                            onValueChange={(value) => updateSchedule(provider, syncName, { frequency: value })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {FREQUENCY_OPTIONS.map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label>Sync Type</Label>
                          <Select
                            value={schedule.sync_type}
                            onValueChange={(value) => updateSchedule(provider, syncName, { sync_type: value })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {SYNC_TYPE_OPTIONS.map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <Switch
                            id={`auto-start-${key}`}
                            checked={schedule.auto_start}
                            onCheckedChange={(checked) => updateSchedule(provider, syncName, { auto_start: checked })}
                          />
                          <Label htmlFor={`auto-start-${key}`}>Auto-start for new connections</Label>
                        </div>

                        <div className="flex items-center space-x-2">
                          <Switch
                            id={`enabled-${key}`}
                            checked={schedule.enabled}
                            onCheckedChange={(checked) => updateSchedule(provider, syncName, { enabled: checked })}
                          />
                          <Label htmlFor={`enabled-${key}`}>Enabled</Label>
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-2 border-t">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Clock className="h-4 w-4" />
                          Last sync: {formatLastSync(provider, syncName)}
                        </div>

                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => triggerSync(provider, syncName)}
                            disabled={triggering[key]}
                          >
                            {triggering[key] ? (
                              <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            ) : (
                              <Play className="h-4 w-4 mr-2" />
                            )}
                            Trigger Now
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => saveSchedule(provider, syncName)}
                            disabled={saving[key]}
                          >
                            {saving[key] ? (
                              <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            ) : (
                              <Save className="h-4 w-4 mr-2" />
                            )}
                            Save Changes
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </Card>
        ))}

        <div className="flex justify-end">
          <Button
            variant="outline"
            onClick={() => {
              loadSchedules();
              loadSyncStatus();
              toast.success('Refreshed sync schedules');
            }}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>
    </AppShell>
  );
}


